import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "q-pa-md" }
const _hoisted_2 = {
  key: 1,
  class: "row justify-center"
}
const _hoisted_3 = { class: "col-md-6 col-sm-9 col-xs-11 q-mx-lg" }
const _hoisted_4 = { class: "row justify-center" }
const _hoisted_5 = { class: "text-h4 q-mb-md" }
const _hoisted_6 = { class: "row" }
const _hoisted_7 = { class: "col-12 flex flex-center" }

import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios'
import { DateTime } from 'luxon'
import { Appointment } from "@/models/appointment";



export default /*@__PURE__*/_defineComponent({
  __name: 'AppointmentPicker',
  props: {
    modelValue: {},
    fetchUrl: {}
  },
  emits: ["update:modelValue", "update:selectedAppointment", "update:appointments"],
  setup(__props: any, { emit: __emit }) {

const props = __props

/* eslint-disable */
const emit = __emit

const isLoading = ref(false)
const appointments = ref<Appointment[]>([])
const selectedAppointment = ref<Appointment | null>(null)

/** q-date erwartet 'YYYY/MM/DD' */
const uiDate = ref<string | null>(null)

/** ---- Lifecycle ---- */
onMounted(load)

/** ---- Laden ---- */
async function load() {
  isLoading.value = true
  try {
    const url = props.fetchUrl ?? 'https://getavailableslots-3uz45qef3a-uc.a.run.app'
    const { data } = await axios.get(url)

    appointments.value = (data as any[]).map((item) => ({
      id: item.id,
      summary: normalizeSummary(item.summary),
      start: new Date(item.start),
      end: new Date(item.end),
    }))

    emit('update:appointments', appointments.value)

    // Voreinstellung: entweder v-model vorwählen oder den ersten freien Termin
    preselectFromModelOrFirst()
  } catch (err) {
    console.error('Fehler beim Laden der Termine:', err)
    appointments.value = []
    emit('update:appointments', appointments.value)
    selectedAppointment.value = null
    emit('update:selectedAppointment', null)
    emit('update:modelValue', null)
  } finally {
    isLoading.value = false
  }
}

/** ---- v-model beobachten: wenn parent ein Appointment setzt, hier vorwählen ---- */
watch(
    () => props.modelValue,
    (appt) => {
      if (!appointments.value.length) return
      if (!appt) {
        selectedAppointment.value = null
        emit('update:selectedAppointment', null)
        return
      }
      // Falls das Objekt nicht exakt aus unserer Liste stammt, per ID „vereinheitlichen“
      const match = appointments.value.find(a => a.id === appt.id) ?? appt
      selectedAppointment.value = match
      uiDate.value = toUIDate(match.start)
      emit('update:selectedAppointment', match)
    },
    { immediate: true }
)

/** ---- Hilfen ---- */
function toUIDate(d: Date) {
  const dt = DateTime.fromJSDate(d)
  return `${dt.year}/${String(dt.month).padStart(2, '0')}/${String(dt.day).padStart(2, '0')}`
}

function formatTime(d: Date) {
  return DateTime.fromJSDate(d).toLocaleString(DateTime.TIME_24_SIMPLE)
}

function formatPanelHeader(uid: string) {
  const [y, m, day] = uid.split('/').map(Number)
  const dt = DateTime.fromObject({ year: y, month: m, day })
  return dt.toLocaleString({ day: '2-digit', month: 'short' })
}

function normalizeSummary(s: string) {
  return isAvailableSummary(s) ? 'Frei' : s
}

function sanitizeSummary(s: string) {
  return s
}

function isAvailableSummary(s: string) {
  const normalized = s?.trim?.().toLowerCase?.() ?? ''
  return normalized === 'frei' || normalized === 'freix' || normalized === 'mal'
}

function isDisabled(a: Appointment) {
  return !isAvailableSummary(a.summary)
}

/** Alle Tage, an denen es Termine gibt (YYYY/MM/DD) */
const eventDays = computed<string[]>(() => {
  const set = new Set(appointments.value.map(a => toUIDate(a.start)))
  return Array.from(set).sort()
})

/** q-date options */
function dateOptionsFn(day: string) {
  return eventDays.value.includes(day)
}

/** Termine eines Tages */
function getAppointmentsOf(uid: string): Appointment[] {
  return appointments.value
      .filter(a => toUIDate(a.start) === uid)
      .sort((a, b) => a.start.getTime() - b.start.getTime())
}

/** Navigation im Kalender */
function onDateNavigation(payload: { year: number; month: number }) {
  return payload
}

/** Pfeil-Tasten unter der Liste */
function selectPrevious() {
  if (!uiDate.value) return
  const idx = eventDays.value.indexOf(uiDate.value)
  if (idx > 0) uiDate.value = eventDays.value[idx - 1]
}

function selectNext() {
  if (!uiDate.value) return
  const idx = eventDays.value.indexOf(uiDate.value)
  if (idx >= 0 && idx < eventDays.value.length - 1) {
    uiDate.value = eventDays.value[idx + 1]
  }
}

/** Auswahl-Handler */
function onSelect(a: Appointment) {
  if (isDisabled(a)) return
  if (selectedAppointment.value?.id === a.id) return

  selectedAppointment.value = a
  uiDate.value = toUIDate(a.start)

  // v-model out: jetzt das vollständige Appointment-Objekt
  emit('update:modelValue', a)
  emit('update:selectedAppointment', a)
}

/** Voreinstellung beim Laden */
function preselectFromModelOrFirst() {
  if (props.modelValue) {
    const match = appointments.value.find(a => a.id === props.modelValue!.id) ?? props.modelValue
    selectedAppointment.value = match
    uiDate.value = toUIDate(match.start)
    emit('update:selectedAppointment', match)
    emit('update:modelValue', match)
    return
  }
  // sonst: ersten freien Termin wählen
  const firstFree = appointments.value.find(a => !isDisabled(a)) || null
  selectedAppointment.value = firstFree
  uiDate.value = firstFree ? toUIDate(firstFree.start) : (eventDays.value[0] ?? null)
  emit('update:selectedAppointment', firstFree ?? null)
  emit('update:modelValue', firstFree ?? null)
}

return (_ctx: any,_cache: any) => {
  const _component_q_spinner = _resolveComponent("q-spinner")!
  const _component_q_date = _resolveComponent("q-date")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_checkbox = _resolveComponent("q-checkbox")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_tab_panel = _resolveComponent("q-tab-panel")!
  const _component_q_tab_panels = _resolveComponent("q-tab-panels")!
  const _directive_ripple = _resolveDirective("ripple")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(_component_q_spinner, {
          key: 0,
          style: {"margin":"auto"},
          height: "300px",
          width: "300px",
          color: "primary"
        }))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", null, [
            _createVNode(_component_q_date, {
              modelValue: uiDate.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((uiDate).value = $event)),
              events: eventDays.value,
              options: dateOptionsFn,
              "event-color": "primary",
              dark: "",
              onNavigation: onDateNavigation
            }, null, 8, ["modelValue", "events"])
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_q_btn, {
                icon: "chevron_left",
                flat: "",
                size: "24px",
                onClick: selectPrevious
              }),
              _createVNode(_component_q_btn, {
                icon: "chevron_right",
                flat: "",
                size: "24px",
                onClick: selectNext
              })
            ]),
            _createVNode(_component_q_tab_panels, {
              dark: "",
              modelValue: uiDate.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((uiDate).value = $event)),
              animated: ""
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(eventDays.value, (ev) => {
                  return (_openBlock(), _createBlock(_component_q_tab_panel, {
                    key: ev,
                    name: ev
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_5, _toDisplayString(formatPanelHeader(ev)), 1),
                      _createVNode(_component_q_list, {
                        separator: "",
                        dark: ""
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getAppointmentsOf(ev), (appointment) => {
                            return _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                              key: appointment.id,
                              clickable: "",
                              disable: isDisabled(appointment),
                              onClick: ($event: any) => (onSelect(appointment))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_item_section, { side: "" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_q_checkbox, {
                                      "model-value": selectedAppointment.value?.id === appointment.id,
                                      onClick: _withModifiers(($event: any) => (onSelect(appointment)), ["stop"])
                                    }, null, 8, ["model-value", "onClick"])
                                  ]),
                                  _: 2
                                }, 1024),
                                _createVNode(_component_q_item_section, { class: "items-center" }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(formatTime(appointment.start)), 1)
                                  ]),
                                  _: 2
                                }, 1024),
                                _createVNode(_component_q_item_section, null, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_6, [
                                      _createElementVNode("div", _hoisted_7, _toDisplayString(sanitizeSummary(appointment.summary)), 1)
                                    ])
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1032, ["disable", "onClick"])), [
                              [_directive_ripple]
                            ])
                          }), 128))
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1032, ["name"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ])
        ]))
  ]))
}
}

})